<template>
  <div class="flex flex-col lg:flex-row h-full w-full bg-n-background overflow-hidden">
                <!-- Main Left Area: Tasks Feed & Top Metrics (Design System 2.0 Responsive Layout) -->
    <div class="flex-1 flex flex-col min-w-0 border-r border-n-weak overflow-hidden">
      
      <!-- Top Navigation Tabs & New Task Button (Row 1) -->
      <div class="flex items-center justify-between px-6 pt-4 pb-3 border-b border-n-weak bg-n-background">
        <div class="flex items-center gap-4 overflow-x-auto no-scrollbar">
          <!-- Tab: Pendentes -->
          <button
            @click="selectTab('pendentes')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap',
              activeTab === 'pendentes'
                ? 'border-[#2781F6] text-[#2781F6]'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-200'
            ]"
          >
            <span>Pendentes</span>
            <span
              v-if="(summary.pending_count || 0) > 0"
              class="px-2 py-0.5 text-xs rounded-full font-black"
              :class="activeTab === 'pendentes' ? 'bg-blue-50 dark:bg-blue-950/40 text-[#2781F6]' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'"
            >
              {{ summary.pending_count }}
            </span>
          </button>

          <!-- Tab: Atrasados / Vencidos -->
          <button
            @click="selectTab('vencidas')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap',
              activeTab === 'vencidas'
                ? 'border-rose-600 text-rose-600 dark:text-rose-400'
                : 'border-transparent text-slate-500 hover:text-rose-500'
            ]"
          >
            <span class="i-lucide-alert-circle size-4" />
            <span>Atrasados / Vencidos</span>
            <span
              v-if="(summary.overdue_count || 0) > 0"
              class="px-2 py-0.5 text-xs rounded-full bg-rose-600 text-white font-extrabold shadow-2xs"
            >
              {{ summary.overdue_count }}
            </span>
          </button>

          <!-- Tab: Para Hoje -->
          <button
            @click="selectTab('hoje')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap',
              activeTab === 'hoje'
                ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                : 'border-transparent text-slate-500 hover:text-amber-500'
            ]"
          >
            <span class="i-lucide-clock size-4 text-amber-500" />
            <span>Para Hoje</span>
            <span
              v-if="(summary.today_count || 0) > 0"
              class="px-2 py-0.5 text-xs rounded-full bg-amber-500 text-slate-950 font-extrabold shadow-2xs"
            >
              {{ summary.today_count }}
            </span>
          </button>

          <!-- Tab: Próximos 7 Dias -->
          <button
            @click="selectTab('semana')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap',
              activeTab === 'semana'
                ? 'border-[#2781F6] text-[#2781F6]'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-200'
            ]"
          >
            <span>Próximos 7 Dias</span>
            <span
              v-if="(summary.week_count || 0) > 0"
              class="px-2 py-0.5 text-xs rounded-full font-bold bg-blue-50 dark:bg-blue-950/40 text-[#2781F6]"
            >
              {{ summary.week_count }}
            </span>
          </button>

          <!-- Tab: Concluídos Mês -->
          <button
            @click="selectTab('concluidas')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap',
              activeTab === 'concluidas'
                ? 'border-emerald-600 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-slate-500 hover:text-emerald-500'
            ]"
          >
            <span class="i-lucide-check-circle-2 size-4 text-emerald-600" />
            <span>Concluídos Mês</span>
            <span
              v-if="(summary.completed_count || 0) > 0"
              class="px-2 py-0.5 text-xs rounded-full bg-emerald-600 text-white font-bold shadow-2xs"
            >
              {{ summary.completed_count }}
            </span>
          </button>

          <!-- Tab: Todas -->
          <button
            @click="selectTab('todas')"
            :class="[
              'pb-2 text-sm font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap',
              activeTab === 'todas'
                ? 'border-[#2781F6] text-[#2781F6]'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-200'
            ]"
          >
            Todas
          </button>
        </div>

        <!-- Botão Nova Tarefa Topo -->
        <div class="flex items-center gap-2 flex-shrink-0 pb-1">
          <button
            @click="showMobileCalendar = true"
            class="lg:hidden flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl bg-[#2781F6]/15 text-[#2781F6] border border-[#2781F6]/30 hover:bg-[#2781F6]/25 transition-all cursor-pointer whitespace-nowrap shadow-2xs"
          >
            <span class="i-lucide-calendar size-4" />
            <span>Calendário</span>
            <span v-if="selectedDate" class="size-2 rounded-full bg-[#2781F6]" />
          </button>

          <button
            @click="openNewTaskModal"
            class="flex items-center gap-1.5 px-3.5 sm:px-4 py-2 text-xs font-bold rounded-xl bg-[#2781F6] text-white hover:bg-[#1c6edb] transition-all shadow-xs cursor-pointer whitespace-nowrap"
          >
            <span class="i-lucide-plus size-4 font-black" />
            <span class="hidden sm:inline">Nova Tarefa / Prazo Fatal</span>
            <span class="sm:hidden">Nova Tarefa</span>
          </button>
        </div>
      </div>

      <!-- Quick Metrics Dashboard Cards (Row 2) -->
      <div class="px-6 py-3 bg-n-background">
        <div class="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <!-- 1. TOTAL DE PRAZOS -->
          <div
            @click="selectTab('todas')"
            class="p-3.5 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] hover:border-[#2781F6]/40 transition-all cursor-pointer flex flex-col justify-between shadow-xs group"
          >
            <div class="flex items-center justify-between text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-bar-chart-2 size-3.5 text-slate-400 group-hover:text-[#2781F6]" />
                TOTAL DE PRAZOS
              </span>
              <span class="i-lucide-info size-3 text-slate-400" />
            </div>
            <div class="text-2xl font-black text-slate-900 dark:text-white mt-1">
              {{ (summary.pending_count || 0) + (summary.completed_count || 0) }}
            </div>
          </div>

          <!-- 2. PARA HOJE -->
          <div
            @click="selectTab('hoje')"
            class="p-3.5 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] hover:border-amber-500/40 transition-all cursor-pointer flex flex-col justify-between shadow-xs group"
          >
            <div class="flex items-center justify-between text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-clock size-3.5 text-amber-500" />
                PARA HOJE
              </span>
              <span class="i-lucide-info size-3 text-slate-400" />
            </div>
            <div class="text-2xl font-black text-slate-900 dark:text-white mt-1">
              {{ summary.today_count || 0 }}
            </div>
          </div>

          <!-- 3. PRÓXIMOS 7 DIAS (DESTAQUE AZUL) -->
          <div
            @click="selectTab('semana')"
            class="p-3.5 rounded-2xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-500/30 hover:border-[#2781F6] transition-all cursor-pointer flex flex-col justify-between shadow-xs group"
          >
            <div class="flex items-center justify-between text-[10px] font-bold text-[#2781F6] uppercase tracking-wider">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-trending-up size-3.5 text-[#2781F6]" />
                PRÓXIMOS 7 DIAS
              </span>
              <span class="i-lucide-info size-3 text-[#2781F6]/60" />
            </div>
            <div class="text-2xl font-black text-[#2781F6] mt-1">
              {{ summary.week_count || 0 }}
            </div>
          </div>

          <!-- 4. ATRASADOS -->
          <div
            @click="selectTab('vencidas')"
            class="p-3.5 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] hover:border-rose-500/40 transition-all cursor-pointer flex flex-col justify-between shadow-xs group"
          >
            <div class="flex items-center justify-between text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-alert-circle size-3.5 text-slate-400 group-hover:text-rose-500" />
                ATRASADOS
              </span>
              <span class="i-lucide-info size-3 text-slate-400" />
            </div>
            <div class="text-2xl font-black text-slate-900 dark:text-white mt-1">
              {{ summary.overdue_count || 0 }}
            </div>
          </div>

          <!-- 5. CONCLUÍDOS MÊS -->
          <div
            @click="selectTab('concluidas')"
            class="p-3.5 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] hover:border-emerald-500/40 transition-all cursor-pointer flex flex-col justify-between shadow-xs group"
          >
            <div class="flex items-center justify-between text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-trophy size-3.5 text-slate-400 group-hover:text-emerald-500" />
                CONCLUÍDOS MÊS
              </span>
              <span class="i-lucide-info size-3 text-slate-400" />
            </div>
            <div class="text-2xl font-black text-slate-900 dark:text-white mt-1">
              {{ summary.completed_count || 0 }}
            </div>
          </div>
        </div>
      </div>

      <!-- Toolbar Area (Row 3 - Fina, Horizontal e Alinhada lado a lado) -->
      <div class="px-6 py-1.5 bg-n-background">
        <div class="px-4 py-2 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] shadow-xs flex items-center justify-between gap-3">
          <!-- Controles da Esquerda: Busca + Prioridades + Prazos Fatais Lado a Lado -->
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <!-- Search Input com Lupa e Botão Limpar -->
            <div class="relative flex-1 min-w-[180px] max-w-md">
              <span class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none" />
              <input
                v-model="searchQuery"
                @input="onSearchInput"
                type="text"
                placeholder="Buscar por título, processo ou cliente..."
                class="w-full h-8.5 bg-slate-50 dark:bg-[#15161a] border border-slate-200 dark:border-[#26282d] rounded-xl pl-8.5 pr-8 py-1 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-[#2781F6] transition-colors"
              />
              <button
                v-if="searchQuery"
                @click="searchQuery = ''; onSearchInput()"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
              >
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>

            <!-- Priority Filter Dropdown -->
            <select
              v-model="filterPriority"
              @change="fetchTarefas"
              class="h-8.5 px-3 text-xs font-medium bg-slate-50 dark:bg-[#15161a] border border-slate-200 dark:border-[#26282d] rounded-xl text-slate-700 dark:text-slate-300 focus:outline-none focus:border-[#2781F6] cursor-pointer shadow-2xs transition-colors shrink-0 w-40"
            >
              <option value="all">Todas as Prioridades</option>
              <option value="urgente">🚨 Urgente</option>
              <option value="alta">⚠️ Alta</option>
              <option value="media">🔵 Média</option>
              <option value="baixa">⚪ Baixa</option>
            </select>

            <!-- Fatal Deadline Switch Checkbox -->
            <label class="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 cursor-pointer font-bold select-none shrink-0 whitespace-nowrap">
              <input
                type="checkbox"
                v-model="filterFatalOnly"
                @change="fetchTarefas"
                class="size-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer"
              />
              <span class="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                ⚡ Apenas Prazos Fatais
              </span>
            </label>
          </div>

          <!-- Controles da Direita: Tag de Data Ativa + Total de Tarefas -->
          <div class="flex items-center gap-3 shrink-0">
            <div v-if="selectedDate" class="flex items-center gap-1">
              <span class="px-2.5 py-1 rounded-full bg-[#2781F6]/15 text-[#2781F6] text-xs font-bold flex items-center gap-1 whitespace-nowrap">
                <span>📅 {{ formatDate(selectedDate) }}</span>
                <button @click="selectCalendarDate('')" class="hover:text-rose-500 cursor-pointer ml-1">✕</button>
              </span>
            </div>

            <div class="text-[11px] font-medium text-slate-400 shrink-0 whitespace-nowrap">
              {{ (meta && meta.total_count) || tarefas.length }} tarefas listadas
            </div>
          </div>
        </div>
      </div>

      <!-- Tasks List Feed (Design System 2.0 Card Table View) -->
      <div class="flex-1 overflow-y-auto px-6 py-3 space-y-3 no-scrollbar">
        
        <!-- Column Headers (Layout Reference) -->
        <div class="grid grid-cols-12 gap-4 px-4 py-1 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
          <div class="col-span-12 md:col-span-5">TAREFA / PROVIDÊNCIA</div>
          <div class="hidden md:block md:col-span-3">PROCESSO / CLIENTE</div>
          <div class="hidden md:block md:col-span-2">PRAZO / FATAL</div>
          <div class="hidden md:block md:col-span-2 text-right">STATUS / AÇÕES</div>
        </div>

        <div v-if="uiFlags.isFetching" class="flex flex-col items-center justify-center p-12 text-slate-400 gap-3">
          <span class="i-lucide-loader-2 size-6 animate-spin text-[#2781F6]" />
          <span class="text-xs font-medium">Carregando tarefas e prazos...</span>
        </div>

        <div v-else-if="tarefas.length === 0" class="flex flex-col items-center justify-center p-16 text-center border border-dashed border-slate-200 dark:border-[#26282d] rounded-2xl bg-white dark:bg-[#1a1b20]">
          <div class="p-4 rounded-full bg-slate-100 dark:bg-[#15161a] text-slate-400 mb-3">
            <span class="i-lucide-calendar-check size-8" />
          </div>
          <h3 class="text-sm font-semibold text-slate-900 dark:text-white mb-1">Nenhuma tarefa encontrada</h3>
          <p class="text-xs text-slate-400 max-w-sm mb-4">
            {{ activeTab === 'vencidas' ? 'Excelente! Não há nenhuma tarefa vencida.' : 'Nenhuma tarefa corresponde aos filtros selecionados.' }}
          </p>
          <button
            @click="openNewTaskModal"
            class="px-3.5 py-1.5 text-xs font-bold rounded-xl bg-[#2781F6] text-white hover:bg-[#1c6edb] transition-all cursor-pointer shadow-xs"
          >
            Criar Nova Tarefa
          </button>
        </div>

        <!-- Task Item Card (Layout with Explicit Solid 6px Left Border & Inner Stripe) -->
        <div
          v-for="task in tarefas"
          :key="task.id"
          @click="openViewTaskModal(task)"
          class="pl-4 pr-4 py-4 rounded-2xl bg-white dark:bg-[#1a1b20] border border-slate-200 dark:border-[#26282d] shadow-xs relative overflow-hidden transition-all hover:border-slate-300 dark:hover:border-[#32343b] hover:shadow-sm cursor-pointer"
          :style="{
            borderLeftWidth: '6px',
            borderLeftStyle: 'solid',
            borderLeftColor: getTaskAccentHex(task)
          }"
        >
          <div class="grid grid-cols-12 gap-4 items-center">
            
            <!-- Coluna 1: TAREFA / PROVIDÊNCIA (Span 5) -->
            <div class="col-span-12 md:col-span-5 space-y-1.5 pr-2">
              <div class="flex items-center gap-2 flex-wrap">
                <h4
                  :class="[
                    'text-sm font-bold leading-snug',
                    task.status === 'concluida' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'
                  ]"
                >
                  {{ task.title }}
                </h4>
                
                <!-- Priority Badge -->
                <span
                  v-if="task.priority === 'urgente'"
                  class="px-2 py-0.5 text-[9px] font-black rounded-md bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-500/30 uppercase"
                >
                  🚨 URGENTE
                </span>
                <span
                  v-else-if="task.priority === 'alta'"
                  class="px-2 py-0.5 text-[9px] font-black rounded-md bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30 uppercase"
                >
                  ⚠️ ALTA
                </span>
                <span
                  v-else-if="task.priority === 'media'"
                  class="px-2 py-0.5 text-[9px] font-bold rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 uppercase"
                >
                  🔵 MÉDIA
                </span>
                <span
                  v-else
                  class="px-2 py-0.5 text-[9px] font-bold rounded-md bg-slate-100 dark:bg-[#15161a] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#26282d] uppercase"
                >
                  ⚪ BAIXA
                </span>

                <!-- Overdue tag -->
                <span
                  v-if="task.overdue"
                  class="px-2 py-0.5 text-[9px] font-black rounded-md bg-rose-600 text-white shadow-2xs"
                >
                  Vencida há {{ task.days_overdue }} dias
                </span>

                <!-- Today tag -->
                <span
                  v-else-if="task.due_date === todayStr && task.status !== 'concluida'"
                  class="px-2 py-0.5 text-[9px] font-black rounded-md bg-amber-500 text-slate-950 shadow-2xs"
                >
                  HOJE
                </span>
              </div>

              <!-- Description -->
              <p v-if="task.description" class="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                {{ task.description }}
              </p>

              <!-- Pills Row: Atendimento & Responsável -->
              <div class="flex items-center gap-2 flex-wrap pt-1">
                <router-link
                  v-if="task.conversation_id"
                  @click.stop
                  :to="{ name: 'inbox_conversation', params: { accountId: currentAccountId, conversation_id: task.conversation_id } }"
                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-[#2781F6] border border-blue-200 dark:border-blue-500/30 text-[11px] font-bold hover:bg-[#2781F6] hover:text-white transition-all cursor-pointer shadow-2xs"
                >
                  <span class="i-lucide-message-circle size-3" />
                  <span>Atendimento #{{ task.conversation_id }}</span>
                </router-link>

                <span
                  v-if="task.assigned_user"
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-slate-100 dark:bg-[#15161a] text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-[#26282d] text-[11px] font-medium"
                >
                  <span class="i-lucide-user size-3 text-slate-400" />
                  <span>Resp: <strong>{{ task.assigned_user.name }}</strong></span>
                </span>
              </div>
            </div>

            <!-- Coluna 2: PROCESSO / CLIENTE (Span 3) -->
            <div class="col-span-12 md:col-span-3 space-y-1">
              <!-- Process Number Link or Sem processo -->
              <div v-if="task.process_number" class="flex items-center gap-1 font-mono text-xs font-bold text-[#2781F6]">
                <span class="i-lucide-scale size-3.5 text-[#2781F6] shrink-0" />
                <span class="truncate">{{ task.process_number }}</span>
              </div>
              <div v-else class="text-xs text-slate-400 italic flex items-center gap-1">
                <span class="i-lucide-slash size-3 text-slate-400" />
                <span>Sem processo</span>
              </div>

              <!-- Client Name & Phone -->
              <div v-if="task.contact">
                <router-link
                  @click.stop
                  :to="{ name: 'contacts_edit', params: { accountId: currentAccountId, contactId: task.contact.id } }"
                  class="text-xs font-bold text-slate-800 dark:text-slate-200 hover:text-[#2781F6] transition-colors block truncate"
                  title="Abrir perfil completo do cliente"
                >
                  {{ task.contact.name }}
                </router-link>
                <span v-if="task.contact.phone_number" class="text-[10px] text-slate-400 block">
                  {{ task.contact.phone_number }}
                </span>
              </div>
            </div>

            <!-- Coluna 3: PRAZO / FATAL (Span 2) -->
            <div class="col-span-6 md:col-span-2 space-y-0.5">
              <div
                :class="[
                  'text-xs font-bold flex items-center gap-1',
                  task.is_fatal_deadline || task.overdue ? 'text-rose-600 dark:text-rose-400' : task.due_date === todayStr ? 'text-amber-600 dark:text-amber-400 font-extrabold' : 'text-slate-800 dark:text-slate-200'
                ]"
              >
                <span class="i-lucide-calendar size-3.5" />
                <span>{{ formatDate(task.due_date) }}</span>
              </div>
              <div v-if="task.due_time" class="text-[10px] text-slate-400 pl-4.5">
                às {{ task.due_time }}
              </div>
              
              <!-- Fatal Badge -->
              <div v-if="task.is_fatal_deadline" class="pt-0.5">
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 text-[9px] font-black uppercase tracking-wider">
                  <span class="i-lucide-zap size-3 fill-rose-500 text-rose-500" />
                  <span>PRAZO FATAL</span>
                </span>
              </div>
            </div>

            <!-- Coluna 4: STATUS / AÇÕES (Span 2) -->
            <div class="col-span-6 md:col-span-2 flex items-center justify-end gap-2">
              <!-- Botão Ver -->
              <button
                @click.stop="openViewTaskModal(task)"
                class="px-3 py-1 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-[#15161a] border border-slate-200 dark:border-[#26282d] hover:bg-slate-100 dark:hover:bg-[#26282d] rounded-xl transition-all cursor-pointer shadow-2xs"
              >
                Ver
              </button>

              <!-- Botão Cumprir / Reabrir -->
              <button
                @click.stop="toggleTaskStatus(task.id)"
                :class="[
                  'px-3.5 py-1 text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs',
                  task.status === 'concluida'
                    ? 'bg-amber-500 hover:bg-amber-600 text-slate-950'
                    : 'bg-[#2781F6] hover:bg-[#1c6edb] text-white'
                ]"
              >
                {{ task.status === 'concluida' ? 'Reabrir' : 'Cumprir' }}
              </button>

              <!-- Botão Editar -->
              <button
                @click.stop="openEditTaskModal(task)"
                class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#26282d] transition-all cursor-pointer"
                title="Editar Tarefa"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>

              <!-- Botão Excluir -->
              <button
                @click.stop="deleteTask(task.id)"
                class="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-all cursor-pointer"
                title="Excluir Tarefa"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Area: Monthly Calendar & Weekly Bar Chart -->
    <!-- Mobile Slide-over Drawer: Calendar & Weekly Chart -->
    <transition name="drawer-fade">
      <div
        v-if="showMobileCalendar"
        class="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-md"
        @click="showMobileCalendar = false"
      />
    </transition>
    <transition name="drawer-slide">
      <div
        v-if="showMobileCalendar"
        class="fixed inset-y-0 right-0 w-full sm:w-96 max-w-[90vw] bg-n-solid-1 z-50 shadow-2xl flex flex-col lg:hidden border-l border-n-weak"
      >
        <div class="p-4 border-b border-n-weak font-bold text-sm text-n-slate-12 flex items-center justify-between bg-n-solid-2">
          <div class="flex items-center gap-2">
            <span class="i-lucide-calendar size-4 text-[#2781F6]" />
            <span>Calendário & Gráficos</span>
          </div>
          <button
            @click="showMobileCalendar = false"
            class="p-1 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="p-5 flex-1 overflow-y-auto space-y-6">
      
      <!-- 1. Monthly Calendar Component -->
      <div class="rounded-2xl border border-n-weak bg-n-background p-4 shadow-xs">
        <div class="flex items-center justify-between mb-3.5">
          <h3 class="text-sm font-bold text-n-slate-12 capitalize flex items-center gap-1.5">
            <span class="i-lucide-calendar size-4 text-[#2781F6]" />
            <span>{{ currentMonthLabel }}</span>
          </h3>

          <div class="flex items-center gap-1">
            <button
              @click="prevMonth"
              class="p-1 rounded-lg hover:bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 transition-all cursor-pointer"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <button
              @click="nextMonth"
              class="p-1 rounded-lg hover:bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 transition-all cursor-pointer"
            >
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>

        <div class="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-n-slate-10 uppercase mb-2">
          <span>Dom</span>
          <span>Seg</span>
          <span>Ter</span>
          <span>Qua</span>
          <span>Qui</span>
          <span>Sex</span>
          <span>Sáb</span>
        </div>

        <div class="grid grid-cols-7 gap-1">
          <button
            v-for="(cell, index) in calendarDays"
            :key="index"
            @click="selectCalendarDate(cell.dateStr)"
            :class="[
              'h-9 flex flex-col items-center justify-center rounded-xl text-xs font-semibold relative transition-all cursor-pointer',
              cell.isToday ? 'border border-[#2781F6] font-bold' : '',
              selectedDate === cell.dateStr
                ? 'bg-[#2781F6] text-white font-black shadow-sm ring-2 ring-[#2781F6]/30'
                : cell.isCurrentMonth
                  ? 'text-n-slate-12 hover:bg-n-solid-1'
                  : 'text-n-slate-8 hover:bg-n-solid-1/50'
            ]"
          >
            <span>{{ cell.dayNum }}</span>

            <div
              v-if="summary.calendar_summary && summary.calendar_summary[cell.dateStr]"
              class="absolute bottom-1 flex items-center justify-center"
            >
              <span
                :class="[
                  'size-1.5 rounded-full',
                  selectedDate === cell.dateStr ? 'bg-white' :
                  summary.calendar_summary[cell.dateStr].fatal > 0 ? 'bg-amber-400 ring-1 ring-amber-500' :
                  summary.calendar_summary[cell.dateStr].pending > 0 ? 'bg-rose-500 ring-1 ring-rose-600' :
                  'bg-emerald-500'
                ]"
              />
            </div>
          </button>
        </div>

        <div v-if="selectedDate" class="mt-3 pt-2.5 border-t border-n-weak flex items-center justify-between">
          <span class="text-xs font-semibold text-[#2781F6]">Dia: {{ formatDate(selectedDate) }}</span>
          <button
            @click="selectCalendarDate('')"
            class="text-[11px] font-bold text-rose-500 hover:underline cursor-pointer"
          >
            Limpar filtro
          </button>
        </div>
      </div>

      <!-- 2. "Publicações da Semana" Bar Chart Card (Always-Visible Quantity & Gradual Bars) -->
      <div class="rounded-2xl border border-n-weak bg-n-background p-4 shadow-xs space-y-3">
        <div>
          <h4 class="text-sm font-bold text-n-slate-12">
            Publicações da Semana
          </h4>
          <p class="text-[11px] text-n-slate-10 leading-tight mt-0.5">
            Quantidade de prazos para cumprir de segunda a domingo
          </p>
        </div>

        <div class="pt-2 pb-1">
          <!-- Bars Container with fixed height -->
          <div class="flex items-end justify-between h-36 pt-4 px-1 border-b border-n-weak/80 relative">
            <div class="absolute inset-x-0 top-4 border-b border-dashed border-n-weak/40 pointer-events-none" />
            <div class="absolute inset-x-0 top-1/2 border-b border-dashed border-n-weak/40 pointer-events-none" />

            <div
              v-for="(day, idx) in weekDistributionData"
              :key="idx"
              class="flex-1 flex flex-col items-center justify-end h-full relative cursor-pointer select-none group"
              @click="selectCalendarDate(day.date)"
            >
              <!-- 1. Always-visible quantity tag above the bar -->
              <div class="mb-1 text-center transition-transform group-hover:scale-105">
                <span
                  v-if="day.count > 0"
                  class="px-1.5 py-0.5 rounded-full text-[10px] font-black leading-tight shadow-xs inline-block"
                  :class="day.fatal_count > 0 ? 'bg-rose-600 text-white' : 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/40'"
                >
                  {{ day.count }}
                </span>
                <span v-else class="text-[11px] text-n-slate-9/30 font-medium select-none">-</span>
              </div>

              <!-- 2. Gradual Solid Bar Track & Bar with explicit height in px -->
              <div class="w-full flex justify-center items-end" style="height: 80px;">
                <div
                  class="w-5 sm:w-6 rounded-t-md transition-all duration-300 relative shadow-2xs"
                  :style="{
                    height: getBarHeightPx(day.count),
                    backgroundColor: day.count > 0 ? (day.fatal_count > 0 ? '#e11d48' : '#f43f5e') : 'rgba(150, 150, 150, 0.2)'
                  }"
                  :class="selectedDate === day.date ? 'ring-2 ring-[#2781F6]' : ''"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between px-1 pt-2 text-[11px] font-bold text-n-slate-11">
            <span
              v-for="(day, idx) in weekDistributionData"
              :key="idx"
              class="flex-1 text-center"
              :class="selectedDate === day.date ? 'text-[#2781F6] font-black' : ''"
            >
              {{ day.day_label }}
            </span>
          </div>
        </div>
      </div>


        </div>

        <div class="p-4 border-t border-n-weak bg-n-solid-2">
          <button
            @click="showMobileCalendar = false"
            class="w-full py-2.5 px-4 bg-[#2781F6] hover:bg-[#2781F6]/90 text-white font-bold text-xs rounded-xl shadow-md cursor-pointer transition-all"
          >
            Ver Tarefas e Prazos
          </button>
        </div>
      </div>
    </transition>
    <!-- Right Area (Desktop only): Monthly Calendar & Weekly Bar Chart -->
    <div class="hidden lg:flex lg:w-96 flex-col bg-n-solid-1 overflow-y-auto no-scrollbar border-l border-n-weak p-5 space-y-6 flex-shrink-0">
      
      <!-- 1. Monthly Calendar Component -->
      <div class="rounded-2xl border border-n-weak bg-n-background p-4 shadow-xs">
        <div class="flex items-center justify-between mb-3.5">
          <h3 class="text-sm font-bold text-n-slate-12 capitalize flex items-center gap-1.5">
            <span class="i-lucide-calendar size-4 text-[#2781F6]" />
            <span>{{ currentMonthLabel }}</span>
          </h3>

          <div class="flex items-center gap-1">
            <button
              @click="prevMonth"
              class="p-1 rounded-lg hover:bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 transition-all cursor-pointer"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <button
              @click="nextMonth"
              class="p-1 rounded-lg hover:bg-n-solid-1 text-n-slate-11 hover:text-n-slate-12 transition-all cursor-pointer"
            >
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>

        <div class="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-n-slate-10 uppercase mb-2">
          <span>Dom</span>
          <span>Seg</span>
          <span>Ter</span>
          <span>Qua</span>
          <span>Qui</span>
          <span>Sex</span>
          <span>Sáb</span>
        </div>

        <div class="grid grid-cols-7 gap-1">
          <button
            v-for="(cell, index) in calendarDays"
            :key="index"
            @click="selectCalendarDate(cell.dateStr)"
            :class="[
              'h-9 flex flex-col items-center justify-center rounded-xl text-xs font-semibold relative transition-all cursor-pointer',
              cell.isToday ? 'border border-[#2781F6] font-bold' : '',
              selectedDate === cell.dateStr
                ? 'bg-[#2781F6] text-white font-black shadow-sm ring-2 ring-[#2781F6]/30'
                : cell.isCurrentMonth
                  ? 'text-n-slate-12 hover:bg-n-solid-1'
                  : 'text-n-slate-8 hover:bg-n-solid-1/50'
            ]"
          >
            <span>{{ cell.dayNum }}</span>

            <div
              v-if="summary.calendar_summary && summary.calendar_summary[cell.dateStr]"
              class="absolute bottom-1 flex items-center justify-center"
            >
              <span
                :class="[
                  'size-1.5 rounded-full',
                  selectedDate === cell.dateStr ? 'bg-white' :
                  summary.calendar_summary[cell.dateStr].fatal > 0 ? 'bg-amber-400 ring-1 ring-amber-500' :
                  summary.calendar_summary[cell.dateStr].pending > 0 ? 'bg-rose-500 ring-1 ring-rose-600' :
                  'bg-emerald-500'
                ]"
              />
            </div>
          </button>
        </div>

        <div v-if="selectedDate" class="mt-3 pt-2.5 border-t border-n-weak flex items-center justify-between">
          <span class="text-xs font-semibold text-[#2781F6]">Dia: {{ formatDate(selectedDate) }}</span>
          <button
            @click="selectCalendarDate('')"
            class="text-[11px] font-bold text-rose-500 hover:underline cursor-pointer"
          >
            Limpar filtro
          </button>
        </div>
      </div>

      <!-- 2. "Publicações da Semana" Bar Chart Card (Always-Visible Quantity & Gradual Bars) -->
      <div class="rounded-2xl border border-n-weak bg-n-background p-4 shadow-xs space-y-3">
        <div>
          <h4 class="text-sm font-bold text-n-slate-12">
            Publicações da Semana
          </h4>
          <p class="text-[11px] text-n-slate-10 leading-tight mt-0.5">
            Quantidade de prazos para cumprir de segunda a domingo
          </p>
        </div>

        <div class="pt-2 pb-1">
          <!-- Bars Container with fixed height -->
          <div class="flex items-end justify-between h-36 pt-4 px-1 border-b border-n-weak/80 relative">
            <div class="absolute inset-x-0 top-4 border-b border-dashed border-n-weak/40 pointer-events-none" />
            <div class="absolute inset-x-0 top-1/2 border-b border-dashed border-n-weak/40 pointer-events-none" />

            <div
              v-for="(day, idx) in weekDistributionData"
              :key="idx"
              class="flex-1 flex flex-col items-center justify-end h-full relative cursor-pointer select-none group"
              @click="selectCalendarDate(day.date)"
            >
              <!-- 1. Always-visible quantity tag above the bar -->
              <div class="mb-1 text-center transition-transform group-hover:scale-105">
                <span
                  v-if="day.count > 0"
                  class="px-1.5 py-0.5 rounded-full text-[10px] font-black leading-tight shadow-xs inline-block"
                  :class="day.fatal_count > 0 ? 'bg-rose-600 text-white' : 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/40'"
                >
                  {{ day.count }}
                </span>
                <span v-else class="text-[11px] text-n-slate-9/30 font-medium select-none">-</span>
              </div>

              <!-- 2. Gradual Solid Bar Track & Bar with explicit height in px -->
              <div class="w-full flex justify-center items-end" style="height: 80px;">
                <div
                  class="w-5 sm:w-6 rounded-t-md transition-all duration-300 relative shadow-2xs"
                  :style="{
                    height: getBarHeightPx(day.count),
                    backgroundColor: day.count > 0 ? (day.fatal_count > 0 ? '#e11d48' : '#f43f5e') : 'rgba(150, 150, 150, 0.2)'
                  }"
                  :class="selectedDate === day.date ? 'ring-2 ring-[#2781F6]' : ''"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between px-1 pt-2 text-[11px] font-bold text-n-slate-11">
            <span
              v-for="(day, idx) in weekDistributionData"
              :key="idx"
              class="flex-1 text-center"
              :class="selectedDate === day.date ? 'text-[#2781F6] font-black' : ''"
            >
              {{ day.day_label }}
            </span>
          </div>
        </div>
      </div>

    </div>
    <!-- Modal: Pop-up de Visualização Completa -->
    <div
      v-if="showViewModal && selectedViewTask"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
    >
      <div class="bg-n-solid-1 border border-n-weak rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4 text-xs text-left max-h-[90vh] flex flex-col">
        <div class="flex items-start justify-between border-b border-n-weak pb-3">
          <div class="space-y-1">
            <div class="flex flex-wrap items-center gap-1.5">
              <span
                :class="[
                  'px-2 py-0.5 text-[10px] font-bold rounded-md uppercase',
                  selectedViewTask.status === 'concluida' ? 'bg-emerald-500 text-white' : 'bg-[#2781F6] text-white'
                ]"
              >
                {{ selectedViewTask.status === 'concluida' ? '✅ CONCLUÍDO' : '⏳ PENDENTE' }}
              </span>

              <span
                v-if="selectedViewTask.is_fatal_deadline"
                class="px-2 py-0.5 text-[10px] font-black rounded-md bg-amber-400 text-black uppercase"
              >
                ⚡ PRAZO FATAL
              </span>

              <span
                v-if="selectedViewTask.overdue"
                class="px-2 py-0.5 text-[10px] font-black rounded-md bg-rose-600 text-white"
              >
                ⚠️ VENCIDA HÁ {{ selectedViewTask.days_overdue }} DIAS
              </span>

              <span
                :class="[
                  'px-2 py-0.5 text-[10px] font-bold rounded-md uppercase',
                  selectedViewTask.priority === 'urgente' ? 'bg-rose-500/20 text-rose-500 border border-rose-500/40' :
                  selectedViewTask.priority === 'alta' ? 'bg-amber-500/20 text-amber-500 border border-amber-500/40' :
                  'bg-[#2781F6]/20 text-[#2781F6] border border-[#2781F6]/40'
                ]"
              >
                Prioridade {{ selectedViewTask.priority }}
              </span>
            </div>
            <h3 class="text-base font-bold text-n-slate-12 leading-snug pt-1">
              {{ selectedViewTask.title }}
            </h3>
          </div>

          <button
            @click="showViewModal = false"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 cursor-pointer shrink-0"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto space-y-3.5 pr-1 no-scrollbar text-xs">
          <div class="grid grid-cols-2 gap-3 p-3 rounded-xl bg-n-solid-2/60 border border-n-weak">
            <div>
              <div class="text-[10px] uppercase font-bold text-n-slate-10">Data Limite</div>
              <div class="text-sm font-bold text-n-slate-12 mt-0.5">
                📅 {{ formatDate(selectedViewTask.due_date) }}
                <span v-if="selectedViewTask.due_time" class="text-xs text-n-slate-10 font-normal">às {{ selectedViewTask.due_time }}</span>
              </div>
            </div>

            <div>
              <div class="text-[10px] uppercase font-bold text-n-slate-10">Processo Vinculado</div>
              <div class="text-xs font-bold text-n-slate-12 mt-0.5">
                ⚖️ {{ selectedViewTask.process_number || 'Não informado' }}
              </div>
            </div>
          </div>

          <div class="space-y-1">
            <div class="text-[10px] uppercase font-bold text-n-slate-10">Descrição / Instruções / Decisão</div>
            <div class="p-3.5 rounded-xl bg-n-background border border-n-weak text-n-slate-12 text-xs leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
              {{ selectedViewTask.description || 'Nenhuma descrição detalhada informada.' }}
            </div>
          </div>

          <!-- Contact & Chat Shortcuts -->
          <div v-if="selectedViewTask.contact" class="p-3 rounded-xl bg-n-solid-2/40 border border-n-weak flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="i-lucide-user size-4 text-[#2781F6]" />
              <div>
                <div class="font-bold text-n-slate-12">{{ selectedViewTask.contact.name }}</div>
                <div class="text-[10px] text-n-slate-10">{{ selectedViewTask.contact.phone_number || selectedViewTask.contact.email || '' }}</div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <router-link
                v-if="selectedViewTask.conversation_id"
                :to="{ name: 'inbox_conversation', params: { accountId: currentAccountId, conversation_id: selectedViewTask.conversation_id } }"
                class="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#2781F6] text-white hover:bg-[#2781F6]/90 transition-all flex items-center gap-1"
              >
                <span class="i-lucide-message-circle size-3" />
                <span>Atendimento</span>
              </router-link>
            </div>
          </div>

          <!-- Assignee -->
          <div v-if="selectedViewTask.assigned_user" class="p-3 rounded-xl bg-n-solid-2/40 border border-n-weak flex items-center gap-2">
            <span class="i-lucide-user-check size-4 text-[#2781F6]" />
            <div class="text-xs">
              <span class="text-n-slate-10">Agente Responsável: </span>
              <strong class="text-n-slate-12">{{ selectedViewTask.assigned_user.name }}</strong>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between pt-3 border-t border-n-weak">
          <div class="flex items-center gap-2">
            <button
              @click="toggleTaskStatus(selectedViewTask.id); showViewModal = false;"
              :class="[
                'px-4 py-2 font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs',
                selectedViewTask.status === 'concluida'
                  ? 'bg-amber-500 text-slate-950 hover:bg-amber-600'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              ]"
            >
              <span :class="selectedViewTask.status === 'concluida' ? 'i-lucide-rotate-ccw' : 'i-lucide-check'" class="size-4" />
              <span>{{ selectedViewTask.status === 'concluida' ? 'Reabrir Prazo' : 'Marcar como Cumprido' }}</span>
            </button>

            <button
              @click="showViewModal = false; openEditTaskModal(selectedViewTask);"
              class="px-3.5 py-2 font-bold rounded-xl bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 transition-all cursor-pointer flex items-center gap-1"
            >
              <span class="i-lucide-pencil size-3.5" />
              <span>Editar</span>
            </button>
          </div>

          <button
            @click="showViewModal = false"
            class="px-4 py-2 rounded-xl bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 font-semibold cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Nova / Editar Tarefa -->
    <div
      v-if="showTaskModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
    >
      <div class="bg-n-solid-1 border border-n-weak rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4 text-xs text-left">
        <div class="flex items-center justify-between border-b border-n-weak pb-3">
          <h3 class="text-base font-bold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-calendar-plus size-5 text-[#2781F6]" />
            <span>{{ editingTaskId ? 'Editar Tarefa / Prazo' : 'Nova Tarefa / Prazo Fatal' }}</span>
          </h3>
          <button
            @click="showTaskModal = false"
            class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 cursor-pointer"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <form @submit.prevent="saveTask" class="space-y-3.5">
          <div class="space-y-1">
            <label class="font-bold text-n-slate-12">Título da Tarefa / Prazo *</label>
            <input
              v-model="taskForm.title"
              required
              placeholder="Ex: Protocolar Recurso Especial, Apresentar Quesitos..."
              class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
            />
          </div>

          <div class="space-y-1">
            <label class="font-medium text-n-slate-11">Descrição / Instruções</label>
            <textarea
              v-model="taskForm.description"
              rows="2"
              placeholder="Instruções jurídicas ou resumo da decisão..."
              class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
            ></textarea>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Data Limite *</label>
              <input
                v-model="taskForm.due_date"
                required
                type="date"
                class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Hora Limite</label>
              <input
                v-model="taskForm.due_time"
                type="time"
                class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Prioridade</label>
              <select
                v-model="taskForm.priority"
                class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6] cursor-pointer"
              >
                <option value="urgente">🚨 Urgente</option>
                <option value="alta">⚠️ Alta</option>
                <option value="media">🔵 Média</option>
                <option value="baixa">⚪ Baixa</option>
              </select>
            </div>

            <div class="space-y-1">
              <label class="font-bold text-n-slate-12">Número do Processo</label>
              <input
                v-model="taskForm.process_number"
                placeholder="Ex: 1000425-20.2026.8.26.0383"
                class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
            </div>
          </div>

          <!-- Responsible Agent Selector -->
          <div class="space-y-1">
            <label class="font-bold text-n-slate-12">Agente Responsável</label>
            <select
              v-model="taskForm.assigned_user_id"
              class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6] cursor-pointer"
            >
              <option :value="null">Nenhum agente atribuído</option>
              <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">
                👤 {{ agent.name }} ({{ agent.email }})
              </option>
            </select>
            <p class="text-[10px] text-n-slate-10">
              * Caso o prazo atinja a data fatal e não seja cumprido, o atendimento será transferido para este agente.
            </p>
          </div>

          <!-- Fatal Deadline Switch -->
          <label class="flex items-center gap-2 p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 font-bold cursor-pointer select-none">
            <input
              type="checkbox"
              v-model="taskForm.is_fatal_deadline"
              class="size-4 rounded text-rose-500 focus:ring-rose-500 cursor-pointer"
            />
            <span>⚡ Prazo Processual Fatal (Perda de Direito)</span>
          </label>

          <!-- Autocomplete Contato -->
          <div class="space-y-1">
            <label class="font-bold text-n-slate-12">Vincular a um Cliente</label>
            <div v-if="selectedContactDisplay" class="flex items-center justify-between p-2.5 rounded-xl bg-[#2781F6]/10 border border-[#2781F6]/30 text-xs">
              <span class="font-semibold text-n-slate-12">{{ selectedContactDisplay }}</span>
              <button type="button" @click="clearSelectedModalContact" class="text-rose-500 font-bold hover:underline cursor-pointer">
                Remover
              </button>
            </div>
            <div v-else class="relative">
              <input
                v-model="modalContactSearch"
                @input="onSearchModalContacts"
                placeholder="Buscar cliente por nome ou telefone..."
                class="w-full px-3 py-2 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6]"
              />
              <div
                v-if="modalContactResults.length"
                class="absolute left-0 right-0 top-full mt-1 bg-n-solid-1 border border-n-weak rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto"
              >
                <div
                  v-for="c in modalContactResults"
                  :key="c.id"
                  @click="selectModalContact(c)"
                  class="p-2 hover:bg-n-alpha-2 cursor-pointer text-xs border-b border-n-weak last:border-0"
                >
                  <div class="font-semibold text-n-slate-12">{{ c.name }}</div>
                  <div class="text-[11px] text-n-slate-10">{{ c.display_label }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Automação Kanban & Nota Privada Interna -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-n-weak">
            <!-- Mover Kanban -->
            <div class="p-2.5 rounded-xl bg-n-solid-2 border border-n-weak space-y-1.5">
              <label class="flex items-center gap-2 text-xs font-bold text-n-slate-12 cursor-pointer select-none">
                <input type="checkbox" v-model="taskForm.move_kanban_stage" class="size-4 rounded border-n-strong text-[#2781F6] focus:ring-[#2781F6]" />
                <span>🔄 Mover para etapa do Funil</span>
              </label>
              <div v-if="taskForm.move_kanban_stage" class="space-y-1">
                <select
                  v-model="taskForm.kanban_stage"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-[#2781F6] cursor-pointer"
                >
                  <option v-for="stage in kanbanStagesList" :key="stage" :value="stage">
                    📌 {{ stage }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Nota Privada -->
            <div class="p-2.5 rounded-xl bg-n-solid-2 border border-n-weak flex flex-col justify-center">
              <label class="flex items-center gap-2 text-xs font-bold text-n-slate-12 cursor-pointer select-none">
                <input type="checkbox" v-model="taskForm.send_private_message" class="size-4 rounded border-n-strong text-[#2781F6] focus:ring-[#2781F6]" />
                <span>🔒 Mandar <strong>nota privada</strong></span>
              </label>
              <span class="text-[10px] text-n-slate-10 pl-6 mt-1">Alerta a equipe interna na conversa</span>
            </div>
          </div>

          <div class="flex justify-end gap-2 pt-3 border-t border-n-weak">
            <button
              type="button"
              @click="showTaskModal = false"
              class="px-4 py-2 rounded-xl bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 font-semibold cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-5 py-2 font-bold rounded-xl bg-[#2781F6] text-white hover:bg-[#2781F6]/90 cursor-pointer shadow-xs"
            >
              Salvar Tarefa
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import TarefasAPI from 'dashboard/api/tarefas';

const store = useStore();

const tarefas = useMapGetter('tarefas/getTarefas');
const meta = useMapGetter('tarefas/getMeta');
const summary = useMapGetter('tarefas/getSummary');
const uiFlags = useMapGetter('tarefas/getUIFlags');

const currentAccountId = computed(() => store.getters['getCurrentAccountId'] || store.getters.getCurrentAccountId || 2);
const agentsList = computed(() => store.getters['agents/getAgents'] || []);

// Tabs & Filters
const activeTab = ref('pendentes');
const searchQuery = ref('');
const filterPriority = ref('all');
const filterFatalOnly = ref(false);

const getBrasiliaTodayStr = () => {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
};
const todayStr = getBrasiliaTodayStr();

// Color Scheme Hex: Vermelho (Urgente/Vencida/Fatal), Amarelo (Alta/Hoje), Azul (Média/Semana), Verde (Concluída/Baixa)
const getTaskAccentHex = (task) => {
  if (task.status === 'concluida') {
    return '#10b981'; // Verde esmeralda
  }
  if (task.overdue || task.is_fatal_deadline || task.priority === 'urgente') {
    return '#ef4444'; // Vermelho vivo
  }
  if (task.due_date === todayStr || task.priority === 'alta') {
    return '#f59e0b'; // Amarelo / Âmbar
  }
  if (task.priority === 'baixa') {
    return '#94a3b8'; // Slate
  }
  return '#2781F6'; // Azul Zéti
};


const isTaskToday = (dateStr) => {
  return dateStr === todayStr;
};

// Indicator Color according to mockup
// Color Scheme: Vermelho (Urgente/Vencida/Fatal), Amarelo (Alta/Hoje), Azul (Média/Semana), Verde (Concluída/Baixa)
const getTaskAccentBg = (task) => {
  if (task.status === 'concluida') {
    return 'bg-emerald-500';
  }
  if (task.overdue || task.is_fatal_deadline || task.priority === 'urgente') {
    return 'bg-rose-500';
  }
  if (task.due_date === todayStr || task.priority === 'alta') {
    return 'bg-amber-500';
  }
  if (task.priority === 'baixa') {
    return 'bg-slate-300 dark:bg-slate-600';
  }
  return 'bg-[#2781F6]';
};

const getIndicatorColor = (task) => {
  if (task.status === 'concluida') return '#10b981'; // Verde esmeralda
  if (task.overdue) return '#ef4444'; // Vermelho vivo
  if (task.is_fatal_deadline) return '#ef4444'; // Vermelho
  if (task.priority === 'urgente') return '#ef4444'; // Vermelho
  if (task.priority === 'alta') return '#f59e0b'; // Âmbar / Laranja
  if (task.due_date === todayStr) return '#f59e0b'; // Âmbar para hoje
  return '#2781F6'; // Azul padrão
};

const selectedDate = ref('');
const dateStart = ref('');
const dateEnd = ref('');

// View Modal State
const showMobileCalendar = ref(false);
const showViewModal = ref(false);
const selectedViewTask = ref(null);

const openViewTaskModal = (task) => {
  selectedViewTask.value = task;
  showViewModal.value = true;
};

// Modal Form State
const showTaskModal = ref(false);
const editingTaskId = ref(null);
const taskForm = ref({
  title: '',
  description: '',
  due_date: todayStr,
  due_time: '08:00',
  priority: 'alta',
  is_fatal_deadline: false,
  process_number: '',
  contact_id: null,
  conversation_id: null,
  assigned_user_id: null,
});

const modalContactSearch = ref('');
const modalContactResults = ref([]);
const selectedContactDisplay = ref('');

// Calendar Navigation State
const calendarYear = ref(new Date().getFullYear());
const calendarMonth = ref(new Date().getMonth());

const currentMonthLabel = computed(() => {
  const d = new Date(calendarYear.value, calendarMonth.value, 1);
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
});

const calendarDays = computed(() => {
  const year = calendarYear.value;
  const month = calendarMonth.value;
  
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  
  const cells = [];
  
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    const m = month === 0 ? 12 : month;
    const y = month === 0 ? year - 1 : year;
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ dayNum: d, dateStr, isCurrentMonth: false, isToday: dateStr === todayStr });
  }
  
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ dayNum: d, dateStr, isCurrentMonth: true, isToday: dateStr === todayStr });
  }
  
  const remaining = 35 - cells.length > 0 ? 35 - cells.length : 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 1 : month + 2;
    const y = month === 11 ? year + 1 : year;
    const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ dayNum: d, dateStr, isCurrentMonth: false, isToday: dateStr === todayStr });
  }
  
  return cells;
});

const prevMonth = () => {
  if (calendarMonth.value === 0) {
    calendarMonth.value = 11;
    calendarYear.value -= 1;
  } else {
    calendarMonth.value -= 1;
  }
};

const nextMonth = () => {
  if (calendarMonth.value === 11) {
    calendarMonth.value = 0;
    calendarYear.value += 1;
  } else {
    calendarMonth.value += 1;
  }
};

const selectCalendarDate = (dateStr) => {
  if (selectedDate.value === dateStr) {
    selectedDate.value = '';
  } else {
    selectedDate.value = dateStr;
  }
  fetchTarefas();
};

const selectTab = (tab) => {
  activeTab.value = tab;
  fetchTarefas();
};

// Weekly distribution bar chart data
const weekDistributionData = computed(() => {
  if (summary.value && summary.value.week_distribution && summary.value.week_distribution.length === 7) {
    return summary.value.week_distribution;
  }
  return [
    { date: '', day_label: 'Seg', count: 0 },
    { date: '', day_label: 'Ter', count: 0 },
    { date: '', day_label: 'Qua', count: 0 },
    { date: '', day_label: 'Qui', count: 0 },
    { date: '', day_label: 'Sex', count: 0 },
    { date: '', day_label: 'Sáb', count: 0 },
    { date: '', day_label: 'Dom', count: 0 },
  ];
});

const maxWeekCount = computed(() => {
  const counts = weekDistributionData.value.map(d => d.count || 0);
  const max = Math.max(...counts, 0);
  return max > 0 ? max : 1;
});

// Gradual and distinct pixel height for each count
const getBarHeightPx = (count) => {
  if (!count || count <= 0) return '6px';
  // Baseline denominator of at least 5 for smooth gradual scaling
  const max = Math.max(maxWeekCount.value, 5);
  const ratio = count / max;
  const heightPx = Math.min(Math.max(Math.round(ratio * 75), 18), 75);
  return `${heightPx}px`;
};

const fetchTarefas = () => {
  const params = {
    tab: activeTab.value,
  };

  if (filterPriority.value !== 'all') params.priority = filterPriority.value;
  if (filterFatalOnly.value) params.is_fatal_deadline = 'true';
  if (selectedDate.value) params.due_date = selectedDate.value;
  if (dateStart.value) params.date_start = dateStart.value;
  if (dateEnd.value) params.date_end = dateEnd.value;
  if (searchQuery.value) params.q = searchQuery.value;

  store.dispatch('tarefas/get', params);
  store.dispatch('tarefas/getSummary');
};

let searchTimeout = null;
const onSearchInput = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchTarefas();
  }, 300);
};

const toggleTaskStatus = (id) => {
  store.dispatch('tarefas/toggleStatus', id);
};

const deleteTask = async (id) => {
  if (confirm('Tem certeza que deseja excluir esta tarefa?')) {
    await store.dispatch('tarefas/delete', id);
    fetchTarefas();
  }
};

// Modal functions
const openNewTaskModal = () => {
  editingTaskId.value = null;
  taskForm.value = {
    title: '',
    description: '',
    due_date: todayStr,
    due_time: '08:00',
    priority: 'alta',
    is_fatal_deadline: false,
    process_number: '',
    contact_id: null,
    conversation_id: null,
    assigned_user_id: null,
  };
  modalContactSearch.value = '';
  modalContactResults.value = [];
  selectedContactDisplay.value = '';
  showTaskModal.value = true;
};

const openEditTaskModal = (task) => {
  editingTaskId.value = task.id;
  taskForm.value = {
    title: task.title,
    description: task.description || '',
    due_date: task.due_date,
    due_time: task.due_time || '',
    priority: task.priority,
    is_fatal_deadline: task.is_fatal_deadline,
    process_number: task.process_number || '',
    contact_id: task.contact?.id || null,
    conversation_id: task.conversation_id || null,
    assigned_user_id: task.assigned_user?.id || null,
  };
  selectedContactDisplay.value = task.contact ? `${task.contact.name} (${task.contact.phone_number || ''})` : '';
  showTaskModal.value = true;
};

let modalSearchTimeout = null;
const onSearchModalContacts = () => {
  clearTimeout(modalSearchTimeout);
  modalSearchTimeout = setTimeout(async () => {
    if (!modalContactSearch.value || modalContactSearch.value.length < 2) {
      modalContactResults.value = [];
      return;
    }
    try {
      const res = await TarefasAPI.searchContacts(modalContactSearch.value);
      modalContactResults.value = res.data || [];
    } catch (e) {
      console.error(e);
    }
  }, 250);
};

const selectModalContact = (contact) => {
  taskForm.value.contact_id = contact.id;
  taskForm.value.conversation_id = contact.conversation_id;
  if (contact.process_number && !taskForm.value.process_number) {
    taskForm.value.process_number = contact.process_number;
  }
  selectedContactDisplay.value = contact.display_label;
  modalContactResults.value = [];
  modalContactSearch.value = '';
};

const clearSelectedModalContact = () => {
  taskForm.value.contact_id = null;
  taskForm.value.conversation_id = null;
  selectedContactDisplay.value = '';
};

const saveTask = async () => {
  try {
    if (editingTaskId.value) {
      await store.dispatch('tarefas/update', { id: editingTaskId.value, data: taskForm.value });
    } else {
      await store.dispatch('tarefas/create', taskForm.value);
    }
    showTaskModal.value = false;
    fetchTarefas();
  } catch (e) {
    alert('Erro ao salvar tarefa: ' + (e.response?.data?.errors?.join(', ') || e.message));
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
};

watch(activeTab, () => {
  fetchTarefas();
});

onMounted(() => {
  fetchTarefas();
  store.dispatch('agents/get');
});
</script>

<style scoped>
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.25s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
